// Org-wide reference library. Header + tip banner + list of type cards.
// Each card is collapsible and contains a drag/drop upload zone plus
// the existing image thumbnails — UI mirrors the Manus prototype so
// contributors don't have to relearn the page.
import { useEffect, useState } from "react";
import { Plus, Info } from "lucide-react";
import { api } from "../../../lib/api";
import { ReferenceTypeForm } from "./ReferenceTypeForm";
import { ReferenceTypeCard } from "./ReferenceTypeCard";

export interface ReferenceImageDto {
  id: number;
  typeId: number;
  blobPath: string;
  filename: string;
  caption: string | null;
  sortOrder: number;
  createdAt: string;
  imageUrl?: string;
}

export interface ReferenceTypeDto {
  id: number;
  name: string;
  description: string | null;
  sortOrder: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  images: ReferenceImageDto[];
}

export function ReferenceLibrary() {
  const [types, setTypes] = useState<ReferenceTypeDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const r = await api<{ types: ReferenceTypeDto[] }>(
        "/api/tools/pipe-takeoff/references",
      );
      setTypes(r.types);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void load(); }, []);

  const upsertType = (t: ReferenceTypeDto) => {
    setTypes((prev) => prev?.map((x) => (x.id === t.id ? t : x)) ?? [t]);
  };
  const removeType = (id: number) => {
    setTypes((prev) => prev?.filter((x) => x.id !== id) ?? []);
  };

  return (
    <>
      <div className="bsp-page-header" style={{ justifyContent: "space-between" }}>
        <h1 className="bsp-page-title">Reference library</h1>
        <button
          type="button"
          className="bsp-btn bsp-btn-primary"
          onClick={() => setAdding(true)}
          disabled={adding}
        >
          <Plus size={14} style={{ marginRight: 6 }} />
          Add type
        </button>
      </div>

      <div className="bsp-card" style={{ background: "var(--bsp-blue-pale)", borderColor: "var(--bsp-blue-tint)", marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
        <Info size={18} style={{ color: "var(--bsp-blue)", marginTop: 2, flex: "0 0 18px" }} />
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          <strong>Tip:</strong> Highlight the component of interest in{" "}
          <span style={{ background: "#FFF59D", padding: "1px 6px", borderRadius: 3 }}>yellow</span>{" "}
          on your screenshots so the AI knows exactly what to look for. Each type can have
          multiple reference images. These images are used as visual examples during extraction.
        </p>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)", marginBottom: 16 }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      {adding && (
        <ReferenceTypeForm
          onSaved={(t) => { setTypes((p) => (p ? [...p, t] : [t])); setAdding(false); }}
          onCancel={() => setAdding(false)}
        />
      )}

      {types === null && !error && <p>Loading…</p>}

      {types && types.length === 0 && !adding && (
        <p style={{ color: "var(--bsp-fg-muted)" }}>
          No reference types yet. Click <strong>Add Type</strong> to create one
          (e.g. 45 Degree Bends, Drains, Flanges).
        </p>
      )}

      {types && types.length > 0 && (
        <div style={{ display: "grid", gap: 12 }}>
          {types.map((t) => (
            <ReferenceTypeCard
              key={t.id}
              type={t}
              onChanged={upsertType}
              onDeleted={removeType}
            />
          ))}
        </div>
      )}
    </>
  );
}
